import { useEffect, useRef, useState } from "react";
import { callFunction, recentApiErrors } from "../api/client";
import { Spinner } from "./Spinner";

/* Feedback from any screen. Phase 1 of docs/plans/feedback-log.md.
 *
 * James told Jesse on 22 September the team would get "a place in the app to
 * leave feedback if something's glitchy". The report has to be quick to send
 * and still say enough to act on, so the person writes one thing and the app
 * attaches where they were and what they had open.
 *
 * Fixed at the bottom right, above everything, as the plan drew it. It was in
 * the top bar for one deploy, and an open record panel put its scrim over the
 * whole top bar, so the button could not be reached with a record open, where
 * a Data report is most likely. The page keeps 4rem free at the bottom and a
 * panel keeps 3rem, so the button covers no content in either.
 *
 * A native <dialog>, so focus stays inside it and Escape closes it. Escape is
 * stopped here, because a record panel listens for it on the document and
 * would close as well. */

type Category = "bug" | "data" | "feature" | "question" | "other";
type Importance = "blocking" | "slowing" | "nice_to_have";

const CATEGORIES: { key: Category; label: string; help: string }[] = [
  { key: "bug", label: "Bug", help: "What were you doing, what happened, and what did you expect?" },
  { key: "data", label: "Data", help: "Which record, what is wrong with it, and how do you know?" },
  { key: "feature", label: "Feature", help: "What do you want it to do, and why?" },
  { key: "question", label: "Question", help: "What are you trying to do?" },
  { key: "other", label: "Other", help: "Anything you want James to know." },
];
const IMPORTANCE: { key: Importance; label: string }[] = [
  { key: "blocking", label: "Blocking me" },
  { key: "slowing", label: "Slowing me down" },
  { key: "nice_to_have", label: "Nice to have" },
];

/* An unsent draft is one person's, in one browser, so browser storage is the
   right place. Wrapped, because a browser that refuses storage still works. */
type Draft = { category: Category | null; body: string; importance: Importance | null };
const EMPTY: Draft = { category: null, body: "", importance: null };
const draftKey = (email: string) => `saleshive.feedback.draft.${email}`;
function readDraft(email: string): Draft {
  try {
    const raw = window.localStorage.getItem(draftKey(email));
    return raw ? { ...EMPTY, ...JSON.parse(raw) } : EMPTY;
  } catch {
    return EMPTY;
  }
}
function writeDraft(email: string, d: Draft | null) {
  try {
    if (d) window.localStorage.setItem(draftKey(email), JSON.stringify(d));
    else window.localStorage.removeItem(draftKey(email));
  } catch {
    /* Storage refused. The draft lives until the tab closes, which is fine. */
  }
}

/* What was open, read off the URL, which is where every screen keeps it. */
const ROUTE_ENTITY: Record<string, string> = {
  companies: "company", people: "person", properties: "property", campaigns: "campaign",
};
function context() {
  const url = new URL(window.location.href);
  const route = url.pathname.split("/").filter(Boolean)[0] ?? "";
  const record = url.searchParams.get("record");
  let entityType: string | null = null;
  let entityId: string | null = null;
  if (route === "runs") {
    entityType = "run";
    entityId = url.pathname.split("/")[2] ?? null;
  } else if (record && ROUTE_ENTITY[route]) {
    entityType = ROUTE_ENTITY[route];
    entityId = record;
  }
  return {
    p_url: url.href,
    p_route: route || "home",
    p_page_title: document.title,
    p_entity_type: entityType,
    p_entity_id: entityId,
    p_app_version: typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "unknown",
    p_user_agent: navigator.userAgent,
    p_viewport: `${window.innerWidth}x${window.innerHeight}`,
    p_recent_errors: recentApiErrors(),
  };
}

export function Feedback({ token, email }: { token: string; email: string }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const text = useRef<HTMLTextAreaElement>(null);
  const [draft, setDraft] = useState<Draft>(() => readDraft(email));
  const [sending, setSending] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [sent, setSent] = useState<number | null>(null);

  const update = (next: Partial<Draft>) => {
    const d = { ...draft, ...next };
    setDraft(d);
    writeDraft(email, d);
  };

  useEffect(() => {
    if (sent === null) return;
    const t = window.setTimeout(() => setSent(null), 6000);
    return () => window.clearTimeout(t);
  }, [sent]);

  const open = () => {
    setProblem(null);
    dialog.current?.showModal();
    text.current?.focus();
  };
  const close = () => dialog.current?.close();

  const ready = draft.category !== null && draft.body.trim() !== "";

  const send = async () => {
    if (!ready || sending) return;
    setSending(true);
    setProblem(null);
    try {
      const id = await callFunction<number>(token, "submit_feedback", {
        p_category: draft.category,
        p_body: draft.body,
        p_importance: draft.importance,
        ...context(),
      });
      writeDraft(email, null);
      setDraft(EMPTY);
      close();
      setSent(id);
    } catch (e) {
      // Kept, not cleared. A report that did not send must not look sent.
      setProblem(`Not sent. ${(e as Error).message}`);
    } finally {
      setSending(false);
    }
  };

  const help = CATEGORIES.find((c) => c.key === draft.category)?.help
    ?? "Choose what type of feedback this is.";

  return (
    <>
      <div className="feedback-launch">
        {sent !== null ? <span className="muted" role="status">Sent as #{sent}. Thank you.</span> : null}
        <button onClick={open} aria-haspopup="dialog">Feedback</button>
      </div>
      <dialog ref={dialog} className="feedback" aria-labelledby="feedback-title"
              onKeyDown={(e) => {
                if (e.key === "Escape") e.stopPropagation();
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void send(); }
              }}>
        <h2 id="feedback-title">Feedback</h2>

        <div className="bar" role="group" aria-label="Type of feedback">
          {CATEGORIES.map((c) => (
            <button type="button" key={c.key}
                    aria-pressed={draft.category === c.key}
                    className={draft.category === c.key ? "pressed" : undefined}
                    onClick={() => update({ category: c.key })}>
              {c.label}
            </button>
          ))}
        </div>

        <label>
          <span className="field-label">What happened, or what you want</span>
          <textarea ref={text} rows={6} value={draft.body}
                    onChange={(e) => update({ body: e.target.value })} />
        </label>
        <p className="hint">{help}</p>
        {draft.body.trim() !== "" && draft.body.trim().length < 20 ? (
          <p className="hint">A sentence about what you were doing helps James find it.</p>
        ) : null}

        <div className="bar" role="group" aria-label="How much it matters">
          <span className="field-label">How much it matters</span>
          {IMPORTANCE.map((i) => (
            <button type="button" key={i.key}
                    aria-pressed={draft.importance === i.key}
                    className={draft.importance === i.key ? "pressed" : undefined}
                    onClick={() => update({ importance: draft.importance === i.key ? null : i.key })}>
              {i.label}
            </button>
          ))}
        </div>

        <p className="hint">The page you are on and what you have open are attached.</p>
        {problem ? <p className="refusal" role="alert">{problem}</p> : null}

        <div className="bar">
          <button type="button" onClick={() => void send()} disabled={!ready || sending}>
            {sending ? <><Spinner />Sending</> : "Send"}
          </button>
          <button type="button" onClick={close}>Close</button>
        </div>
      </dialog>
    </>
  );
}
